import { useState } from 'react';
import { useQuery } from 'react-query';
/** Components */
import { Container } from '../../Components/Container';
import { DefaultTable } from 'Components/DefaultTable';
import { Information } from 'Components/Information';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { tableHeader } from './table-header.js';
/** Services */
import { deleteOffice, getOfficesPaged } from 'services/offices-service';
/** State Managers */
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
/** Third Library Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import { Pagination, Stack } from '@mui/material';
import { PageHelper } from '../../Components/PageHelper';

export const Offices = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const {
    isLoading,
    error,
    data: pagedOffices,
  } = useQuery(['getOfficesPaged', reFetch, page], async () => {
    const result = await getOfficesPaged(selectedTenant.slug, page, 15);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteOffice(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!reFetch);
        SwalReact.fire({
          title: 'Sucesso!',
          text: 'Escritório removido com sucesso',
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu algum erro ao tentar remove o Escritório!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  // Configuração dos passos do tour interativo
  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo à página de Escritórios! Aqui você gerencia todas as localidades físicas onde sua empresa possui representação.',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="info-section"]',
      content: 'Esta seção explica o objetivo da página: cadastrar e gerenciar os escritórios e representações físicas da empresa.',
      placement: 'bottom',
      title: 'ℹ️ Informações',
    },
    {
      target: '[data-tour="add-button"]',
      content: 'Clique aqui para adicionar um novo escritório. Você poderá cadastrar nome, endereço e outras informações relevantes.',
      placement: 'bottom',
      title: '➕ Adicionar Escritório',
    },
    {
      target: '[data-tour="offices-table"]',
      content: 'Aqui são listados todos os escritórios cadastrados. Você pode visualizar detalhes, editar ou excluir cada registro.',
      placement: 'top',
      title: '📋 Lista de Escritórios',
    },
    {
      target: '[data-tour="pagination"]',
      content: 'Use a paginação para navegar entre diferentes páginas quando houver muitos escritórios cadastrados.',
      placement: 'top',
      title: '📄 Paginação',
    },
    {
      target: 'body',
      content: 'Pronto! Agora você sabe como gerenciar os escritórios da empresa. Use o botão de ajuda (?) para rever este tour quando precisar.',
      placement: 'center',
      title: '✨ Tour Concluído!',
    },
  ];

  return (
    <Container title="Escritórios" breadcrumb="Gerenciar / Escritórios" showButtonAdd linkPage="/offices">
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="offices-tour"
        autoStart={true}
        showButton={true}
      />
      
      <div data-tour="info-section">
      <Information
        title="Escritórios"
        description={`Cadastre as localidades em que a empresa possui escritórios e representação física.`}
        style={{
          marginTop: '24px',
        }}
      />
      </div>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedOffices.data?.length === 0 && (
        <NoData description={'Não há Escritórios para exibir'} />
      )}
      {!isLoading && !error && pagedOffices.data?.length > 0 && (
        <div className="container">
          <div data-tour="offices-table">
          <DefaultTable rows={tableHeader} cells={pagedOffices.data} onClickDelete={onDelete} link="offices" />
          </div>
          <div data-tour="pagination">
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedOffices.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir <strong>{` ${itemToDelete?.name} `}</strong> como Escritório?
              </>
            }
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
    </Container>
  );
};
